import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Gift, Trophy, Pencil, Trash2, Check, X, Loader2, History } from "lucide-react";
import { api, formatApiError } from "../../lib/api";

export default function AdminReferrals() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editStatus, setEditStatus] = useState("pending");
  const [editAmount, setEditAmount] = useState("5");
  const [busyId, setBusyId] = useState(null);
  const [historyFor, setHistoryFor] = useState(null); // referral row
  const [historyRows, setHistoryRows] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  async function openHistory(r) {
    setHistoryFor(r);
    setHistoryRows(null);
    setHistoryLoading(true);
    try {
      const { data } = await api.get(`/admin/referrals/${r.id}/audit`);
      setHistoryRows(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
      setHistoryRows([]);
    } finally {
      setHistoryLoading(false);
    }
  }

  function closeHistory() {
    setHistoryFor(null);
    setHistoryRows(null);
  }

  async function refresh() {
    try {
      const { data } = await api.get("/admin/referrals");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, []);

  function startEdit(r) {
    setEditingId(r.id);
    setEditStatus(r.status || "pending");
    setEditAmount(String(r.amount ?? 5));
  }

  function cancelEdit() {
    setEditingId(null);
  }

  async function saveEdit(r) {
    setBusyId(r.id);
    try {
      await api.patch(`/admin/referrals/${r.id}`, {
        status: editStatus,
        amount: Number(editAmount) || 0,
      });
      toast.success("Indicação atualizada.");
      setEditingId(null);
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  }

  async function remove(r) {
    if (!window.confirm(`Eliminar indicação de ${r.referred_name || r.referred_email}? Esta ação não pode ser desfeita.`)) {
      return;
    }
    setBusyId(r.id);
    try {
      await api.delete(`/admin/referrals/${r.id}`);
      toast.success("Indicação eliminada.");
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  }

  if (loading) return <div className="text-sm text-[#a0b2af]">A carregar…</div>;
  if (!data) return null;

  const { leaderboard, totals } = data;

  return (
    <div className="space-y-8" data-testid="admin-referrals-page">
      <header>
        <div className="eyebrow text-[#d4af37] flex items-center gap-2">
          <Gift size={14} /> Admin · Indicações
        </div>
        <h1 className="font-display text-4xl sm:text-5xl mt-2">Programa de indicações</h1>
        <p className="text-sm text-[#a0b2af] mt-3 max-w-2xl">
          Monitorize e edite as indicações ativas. Pode alterar o estado, o valor
          do desconto ou eliminar uma entrada se necessário.
        </p>
      </header>

      {/* TOTALS */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Indicações totais" value={totals.total_referrals} />
        <Stat label="A aguardar" value={totals.pending} color="amber" />
        <Stat label="Desbloqueadas" value={totals.unlocked} color="gold" />
        <Stat label="Já descontadas" value={totals.consumed}
              hint={`${(totals.money_given || 0).toFixed(0)}€ em descontos`}
              color="green" />
      </section>

      {/* LEADERBOARD */}
      <section>
        <div className="eyebrow text-[#a0b2af] mb-3 flex items-center gap-2">
          <Trophy size={14} /> Top referenciadores
        </div>
        {leaderboard.length === 0 ? (
          <div className="lux-card p-8 text-center text-sm text-[#a0b2af]"
               data-testid="admin-referrals-empty">
            Ainda não há indicações registadas.
          </div>
        ) : (
          <div className="space-y-2" data-testid="admin-referrals-leaderboard">
            {leaderboard.map((r, i) => (
              <div key={r.referrer_id}
                   className={`lux-card p-4 flex items-center gap-4 ${i === 0 ? "border-[#d4af37]/60 bg-[#d4af37]/5" : ""}`}
                   data-testid={`admin-referrer-${r.referrer_id}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-display text-lg ${i === 0 ? "bg-[#d4af37] text-[#010c0a]" : "bg-[#062c27] text-[#a0b2af]"}`}>
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[#fdfbf7] truncate">{r.name || "—"}</div>
                  <div className="text-xs text-[#a0b2af] truncate">
                    {r.email} · <span className="font-mono text-[#d4af37]">{r.referral_code}</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-display text-xl text-[#d4af37]">{r.total}</div>
                  <div className="text-[10px] uppercase tracking-widest text-[#a0b2af]">
                    <span className="text-[#f59e0b]">{r.pending}p</span> ·{" "}
                    <span className="text-[#d4af37]">{r.unlocked}u</span> ·{" "}
                    <span className="text-[#10b981]">{r.consumed}c</span>
                  </div>
                  <div className="text-[10px] text-[#a0b2af] mt-0.5">
                    {(r.total_discount_given || 0).toFixed(0)}€
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* RECENT — editable */}
      <section>
        <div className="eyebrow text-[#a0b2af] mb-3 flex items-center gap-2">
          <Users size={14} /> Todas as indicações
        </div>
        {data.all.length === 0 ? (
          <div className="lux-card p-6 text-center text-sm text-[#a0b2af]">
            Nenhuma indicação para editar.
          </div>
        ) : (
          <div className="space-y-1" data-testid="admin-referrals-recent">
            {data.all.map((r) => {
              const isEditing = editingId === r.id;
              const isBusy = busyId === r.id;
              return (
                <div key={r.id}
                     className="lux-card p-3 flex items-center justify-between gap-3 text-sm flex-wrap"
                     data-testid={`admin-referral-row-${r.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-[#fdfbf7] truncate">{r.referred_name || "—"}</div>
                    <div className="text-xs text-[#a0b2af] truncate">{r.referred_email}</div>
                  </div>

                  {isEditing ? (
                    <div className="flex items-center gap-2 flex-wrap">
                      <select
                        value={editStatus}
                        onChange={(e) => setEditStatus(e.target.value)}
                        className="bg-[#041f1b] border border-[#1a4640] text-xs px-2 py-1.5 text-[#fdfbf7] focus:border-[#d4af37] focus:outline-none"
                        data-testid={`admin-referral-status-${r.id}`}
                      >
                        <option value="pending">Pendente</option>
                        <option value="unlocked">Desbloqueada</option>
                        <option value="consumed">Aplicada</option>
                      </select>
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          min={0}
                          step="0.5"
                          value={editAmount}
                          onChange={(e) => setEditAmount(e.target.value)}
                          className="w-20 bg-[#041f1b] border border-[#1a4640] text-xs px-2 py-1.5 text-[#fdfbf7] focus:border-[#d4af37] focus:outline-none"
                          data-testid={`admin-referral-amount-${r.id}`}
                        />
                        <span className="text-xs text-[#a0b2af]">€</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => saveEdit(r)}
                        disabled={isBusy}
                        className="inline-flex items-center gap-1 px-3 py-1.5 border border-[#10b981]/40 text-[#10b981] text-xs uppercase tracking-widest hover:bg-[#10b981]/10 disabled:opacity-50"
                        data-testid={`admin-referral-save-${r.id}`}
                      >
                        {isBusy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Guardar
                      </button>
                      <button
                        type="button"
                        onClick={cancelEdit}
                        disabled={isBusy}
                        className="inline-flex items-center gap-1 px-3 py-1.5 border border-[#1a4640] text-[#a0b2af] text-xs uppercase tracking-widest hover:border-[#d4af37] hover:text-[#d4af37] disabled:opacity-50"
                        data-testid={`admin-referral-cancel-${r.id}`}
                      >
                        <X size={12} /> Cancelar
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-[#d4af37] font-mono">
                        {(r.amount ?? 5).toFixed(0)}€
                      </span>
                      <StatusBadge status={r.status} />
                      <button
                        type="button"
                        onClick={() => openHistory(r)}
                        disabled={isBusy}
                        title="Histórico de alterações"
                        className="p-1.5 border border-[#1a4640] text-[#a0b2af] hover:border-[#d4af37] hover:text-[#d4af37] transition-colors disabled:opacity-50"
                        data-testid={`admin-referral-history-${r.id}`}
                      >
                        <History size={12} />
                      </button>
                      <button
                        type="button"
                        onClick={() => startEdit(r)}
                        disabled={isBusy}
                        title="Editar"
                        className="p-1.5 border border-[#1a4640] text-[#a0b2af] hover:border-[#d4af37] hover:text-[#d4af37] transition-colors disabled:opacity-50"
                        data-testid={`admin-referral-edit-${r.id}`}
                      >
                        <Pencil size={12} />
                      </button>
                      <button
                        type="button"
                        onClick={() => remove(r)}
                        disabled={isBusy}
                        title="Eliminar"
                        className="p-1.5 border border-[#1a4640] text-[#a0b2af] hover:border-[#ef4444] hover:text-[#ef4444] transition-colors disabled:opacity-50"
                        data-testid={`admin-referral-delete-${r.id}`}
                      >
                        {isBusy ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {historyFor && (
        <HistoryModal
          referral={historyFor}
          rows={historyRows}
          loading={historyLoading}
          onClose={closeHistory}
        />
      )}
    </div>
  );
}

function HistoryModal({ referral, rows, loading, onClose }) {
  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/60" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div className="lux-card bg-[#041f1b] border border-[#1a4640] w-full max-w-lg max-h-[80vh] overflow-auto pointer-events-auto"
             data-testid="admin-referral-history-modal">
          <div className="flex items-center justify-between px-5 py-4 border-b border-[#1a4640] sticky top-0 bg-[#041f1b]">
            <div>
              <div className="eyebrow text-[#d4af37] flex items-center gap-2">
                <History size={14} /> Histórico
              </div>
              <div className="text-sm text-[#fdfbf7] mt-1 truncate">
                {referral.referred_name || referral.referred_email}
              </div>
            </div>
            <button type="button" onClick={onClose}
                    className="p-2 hover:text-[#d4af37] text-[#a0b2af]"
                    data-testid="admin-referral-history-close">
              <X size={16} />
            </button>
          </div>
          <div className="p-5">
            {loading ? (
              <div className="text-sm text-[#a0b2af] flex items-center gap-2">
                <Loader2 size={14} className="animate-spin" /> A carregar…
              </div>
            ) : rows && rows.length === 0 ? (
              <div className="text-sm text-[#a0b2af] text-center py-8">
                Esta indicação ainda não tem alterações registadas.
              </div>
            ) : (
              <ol className="space-y-3">
                {(rows || []).map((a) => (
                  <li key={a.id} className="border-l-2 border-[#d4af37]/40 pl-4 py-1"
                      data-testid={`admin-referral-history-entry-${a.id}`}>
                    <div className="text-[10px] uppercase tracking-widest text-[#a0b2af]">
                      {formatWhen(a.created_at)} · {a.admin_name || "Admin"}
                    </div>
                    <div className="text-sm text-[#fdfbf7] mt-0.5">
                      {renderAction(a)}
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

function formatWhen(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
}

const STATUS_LABEL = {
  pending: "Pendente",
  unlocked: "Desbloqueada",
  consumed: "Aplicada",
};

function renderAction(a) {
  if (a.action === "delete") {
    return <span className="text-[#ef4444]">Indicação eliminada</span>;
  }
  const parts = [];
  const c = a.changes || {};
  if (c.amount) {
    parts.push(
      `valor: ${Number(c.amount.from ?? 0).toFixed(2)}€ → ${Number(c.amount.to ?? 0).toFixed(2)}€`
    );
  }
  if (c.status) {
    parts.push(
      `estado: ${STATUS_LABEL[c.status.from] || c.status.from} → ${STATUS_LABEL[c.status.to] || c.status.to}`
    );
  }
  return parts.length ? parts.join(" · ") : "Alteração guardada";
}

function Stat({ label, value, color, hint }) {
  const colorMap = {
    gold: "text-[#d4af37]",
    green: "text-[#10b981]",
    amber: "text-[#f59e0b]",
  };
  return (
    <div className="lux-card p-4">
      <div className="eyebrow text-[10px] text-[#a0b2af]">{label}</div>
      <div className={`font-display text-3xl mt-2 ${colorMap[color] || "text-[#fdfbf7]"}`}>
        {value}
      </div>
      {hint && <div className="text-xs text-[#a0b2af] mt-1">{hint}</div>}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: { label: "Pendente", cls: "border-[#f59e0b]/40 text-[#f59e0b]" },
    unlocked: { label: "Desbloqueada", cls: "border-[#d4af37]/50 text-[#d4af37]" },
    consumed: { label: "Aplicada", cls: "border-[#10b981]/40 text-[#10b981]" },
  };
  const s = map[status] || { label: status, cls: "border-[#1a4640] text-[#a0b2af]" };
  return (
    <span className={`px-3 py-1 text-[10px] uppercase tracking-widest border ${s.cls}`}>
      {s.label}
    </span>
  );
}
